<script setup lang="ts">
import type { ChatMessage } from '~/models/Content/ContentChatbot'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { renderMarkdownWithMath } from '~/utils/markdown'

const props = defineProps<{
  message: ChatMessage
  autoFocus: boolean
  showRecallNotice?: boolean
}>()

const { t } = useI18n()

const renderedMessage = computed(() => renderMarkdownWithMath(props.message.message))

const messageClasses = computed(() => [
  props.message.from,
  props.message.from === 'assistant'
    ? 'animate-fade-in-right mr-4 justify-self-start rounded-bl-[2px] bg-(--theme-5,white) text-(--theme-60,black) sm:mr-8 md:mr-10'
    : 'animate-fade-in-left ml-4 justify-self-end rounded-br-[2px] bg-(--theme-50,#FFC483) text-(--theme-5,black) sm:ml-8 md:ml-10',
  props.autoFocus && 'prose-lg',
])
</script>

<template>
  <div
    class="message prose prose-p:my-0 prose-headings:first:mt-0 prose-headings:my-2 relative rounded-xl p-4"
    :class="messageClasses"
  >
    <div
      v-dompurify-html="renderedMessage"
      :aria-label="message.from === 'assistant' ? t('chatbot.answer') : t('chatbot.message')"
    />
  </div>
  <div
    v-if="showRecallNotice"
    class="text-gray-40 justify-self-center"
    v-text="t('chatbot.remember')"
  />
</template>

<style scoped>
.prose {
  --tw-prose-bullets: var(--theme-60, #000);
  --tw-prose-counters: var(--theme-60, #000);
  --tw-prose-bold: var(--theme-60, #000);
  --tw-prose-th-borders: var(--theme-60, #000);
  --tw-prose-td-borders: var(--theme-50, #000);

  :deep(math) {
    font-size: 1.35em;
  }
}

:deep(.latex-formula-button) {
  display: inline-block;
  padding: 2px 8px;
  margin: 0 2px;
  background: var(--theme-10, #e0e0e0);
  border: 1px solid var(--theme-20, #ccc);
  border-radius: 4px;
  cursor: pointer;
  font-family: monospace;
  transition: background 0.2s;
}

:deep(.latex-formula-button:hover) {
  background: var(--theme-20, #d0d0d0);
}
</style>
